<template>
  <div class="asset-hierarchy-view">
    <div class="d-flex align-items-center justify-content-between mb-4">
      <div class="d-flex align-items-center gap-2">
        <i class="bi bi-diagram-2-fill fs-3 text-primary" />
        <div>
          <h3 class="mb-0">
            {{ $t('components.assetHierarchy.title') }}
          </h3>
          <div class="text-muted small">
            {{ $t('components.assetHierarchy.subtitle') }}
          </div>
        </div>
      </div>
      <button
        class="btn btn-light-primary d-flex align-items-center gap-2"
        @click="fetchTree"
        :disabled="loading"
      >
        <i
          class="bi bi-arrow-repeat"
          :class="{ 'fa-spin': loading }"
        />
        <span>{{ $t('components.assetHierarchy.refresh') }}</span>
      </button>
    </div>

    <div
      v-if="error"
      class="alert alert-danger d-flex align-items-center gap-2"
    >
      <i class="bi bi-exclamation-triangle-fill" />
      <span>{{ error }}</span>
    </div>

    <div
      v-if="loading"
      class="d-flex align-items-center gap-3 py-4"
    >
      <span
        class="spinner-border text-primary"
        role="status"
      />
      <span>{{ $t('components.assetHierarchy.loading') }}</span>
    </div>

    <div
      v-else
      class="tree-card"
    >
      <div
        v-if="!tree.length"
        class="text-muted"
      >
        {{ $t('components.assetHierarchy.empty') }}
      </div>
      <ul
        v-else
        class="tree-list"
      >
        <HierarchyNode
          v-for="node in tree"
          :key="node.id"
          :node="node"
          :expanded-set="expanded"
          @toggle="toggleNode"
        />
      </ul>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, h, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { getAssetGroupsTree } from '@/crud/asset-group'

type TreeNode = {
  id: number
  name: string
  parentId: number | null
  hierarchyName?: string | null
  depth?: number
  isActive?: boolean
  assetsTotal?: number
  location?: {
    street?: string | null
    city?: string | null
    postalCode?: string | null
    country?: string | null
    countryCode?: string | null
    lat?: string | null
    lng?: string | null
  } | null
  assets?: Array<{ id: number; name?: string | null; serialNumber?: string | null }>
  vehicles?: Array<{ id: number; registration?: string | null; imei?: string | null }>
  users?: Array<{ id: number; email?: string; roles?: string[] }>
  children?: TreeNode[]
}

const HierarchyNode = defineComponent({
  name: 'HierarchyNode',
  props: {
    node: {
      type: Object as () => TreeNode,
      required: true
    },
    expandedSet: {
      type: Object as () => Set<number>,
      required: true
    }
  },
  emits: ['toggle'],
  setup(props, { emit }) {
    const { t } = useI18n()
    const isExpanded = computed(() => props.expandedSet.has(props.node.id))
    const toggle = () => emit('toggle', props.node.id)

    return () => {
      const hasChildren = !!(props.node.children && props.node.children.length)
      const hasAssets = !!(props.node.assets && props.node.assets.length)
      const hasVehicles = !!(props.node.vehicles && props.node.vehicles.length)
      const hasUsers = !!(props.node.users && props.node.users.length)
      const hasLocation = !!props.node.location
      const showDetails = isExpanded.value || !hasChildren

      const detailBlocks: any[] = []

      if (hasLocation && props.node.location) {
        const loc = props.node.location
        const addressParts = [
          loc.street,
          [loc.postalCode, loc.city].filter(Boolean).join(' '),
          [loc.countryCode || loc.country].filter(Boolean).join(' ')
        ].filter(Boolean)

        const coords =
          loc.lat && loc.lng ? `(${Number(loc.lat).toFixed(5)}, ${Number(loc.lng).toFixed(5)})` : null

        detailBlocks.push(
          h('div', { class: 'detail-block' }, [
            h('div', { class: 'detail-title' }, [
              h('i', { class: 'bi bi-geo-alt-fill me-2 text-danger' }),
              t('components.assetHierarchy.location')
            ]),
            h('div', { class: 'detail-text' }, addressParts.join(', ') || t('components.assetHierarchy.noAddress')),
            coords ? h('div', { class: 'detail-text text-muted small' }, coords) : null
          ])
        )
      }

      if (hasUsers) {
        detailBlocks.push(
          h('div', { class: 'detail-block' }, [
            h('div', { class: 'detail-title' }, [
              h('i', { class: 'bi bi-people me-2 text-primary' }),
              t('components.assetHierarchy.users')
            ]),
            h(
              'ul',
              { class: 'detail-list' },
              props.node.users!.map(user =>
                h('li', { class: 'detail-item', key: user.id }, [
                  h('div', { class: 'fw-semibold' }, user.email),
                  h('div', { class: 'text-muted small' }, (user.roles || []).join(', '))
                ])
              )
            )
          ])
        )
      }

      if (hasAssets) {
        detailBlocks.push(
          h('div', { class: 'detail-block' }, [
            h('div', { class: 'detail-title' }, [
              h('i', { class: 'bi bi-hdd-network me-2 text-success' }),
              t('components.assetHierarchy.assets')
            ]),
            h(
              'ul',
              { class: 'detail-list' },
              props.node.assets!.map(asset =>
                h('li', { class: 'detail-item', key: asset.id }, [
                  h(
                    'div',
                    { class: 'fw-semibold' },
                    asset.name
                      ? `${asset.name}${asset.serialNumber ? ` (${asset.serialNumber})` : ''}`
                      : `#${asset.id}`
                  )
                ])
              )
            )
          ])
        )
      }

      if (hasVehicles) {
        detailBlocks.push(
          h('div', { class: 'detail-block' }, [
            h('div', { class: 'detail-title' }, [
              h('i', { class: 'bi bi-truck me-2 text-warning' }),
              t('components.assetHierarchy.vehicles')
            ]),
            h(
              'ul',
              { class: 'detail-list' },
              props.node.vehicles!.map(vehicle =>
                h('li', { class: 'detail-item', key: vehicle.id }, [
                  h('div', { class: 'fw-semibold' }, vehicle.registration || `#${vehicle.id}`),
                  vehicle.imei ? h('div', { class: 'text-muted small' }, vehicle.imei) : null
                ])
              )
            )
          ])
        )
      }

      if (hasChildren) {
        detailBlocks.push(
          h(
            'ul',
            { class: 'tree-list tree-list--child' },
            props.node.children!.map(child =>
              h(HierarchyNode, {
                key: child.id,
                node: child,
                expandedSet: props.expandedSet,
                onToggle: (id: number) => emit('toggle', id)
              })
            )
          )
        )
      }

      return h('li', { class: 'tree-node' }, [
        h('div', { class: 'tree-node__row' }, [
          hasChildren
            ? h(
                'button',
                { class: 'btn btn-icon btn-sm btn-light me-2', onClick: toggle },
                [h('i', { class: ['bi', isExpanded.value ? 'bi-dash-lg' : 'bi-plus-lg'] })]
              )
            : h('span', { class: 'tree-node__dot me-2' }),
          h('div', { class: 'flex-grow-1' }, [
            h('div', { class: 'fw-semibold' }, props.node.name),
            h('div', { class: 'text-muted small' }, [
              props.node.hierarchyName || '—',
              props.node.isActive === false ? ' · ' + t('components.assetHierarchy.inactive') : ''
            ]),
            h('div', { class: 'd-flex align-items-center gap-2 mt-1' }, [
              h('span', { class: 'badge bg-light-primary text-primary' }, [
                h('i', { class: 'bi bi-people me-1' }),
                (props.node.users?.length ?? 0) + ' ' + t('components.assetHierarchy.users')
              ]),
              h('span', { class: 'badge bg-light-success text-success' }, [
                h('i', { class: 'bi bi-hdd-network me-1' }),
                (props.node.assetsTotal ?? props.node.assets?.length ?? 0) + ' ' + t('components.assetHierarchy.assets')
              ]),
              h('span', { class: 'badge bg-light-warning text-warning' }, [
                h('i', { class: 'bi bi-truck me-1' }),
                (props.node.vehicles?.length ?? 0) + ' ' + t('components.assetHierarchy.vehicles')
              ]),
              hasLocation
                ? h('span', { class: 'badge bg-light-danger text-danger' }, [
                    h('i', { class: 'bi bi-geo-alt-fill me-1' }),
                    t('components.assetHierarchy.hasLocation')
                  ])
                : null
            ])
          ])
        ]),
        showDetails && detailBlocks.length
          ? h('div', { class: 'tree-node__details' }, detailBlocks)
          : null
      ])
    }
  }
})

export default defineComponent({
  name: 'AssetHierarchyView',
  components: { HierarchyNode },
  emits: ['pageName'],
  data() {
    return {
      tree: [] as TreeNode[],
      loading: false,
      error: null as string | null,
      expanded: new Set<number>()
    }
  },
  watch: {
    '$i18n.locale'() {
      this.setTitle()
    }
  },
  mounted() {
    this.setTitle()
    this.fetchTree()
  },
  methods: {
    setTitle() {
      this.$emit('pageName', this.$t('aside.menu.pages.assetHierarchy'))
    },

    toggleNode(id: number) {
      if (this.expanded.has(id)) {
        this.expanded.delete(id)
      } else {
        this.expanded.add(id)
      }
      this.expanded = new Set(this.expanded)
    },

    async fetchTree() {
      this.loading = true
      this.error = null
      try {
        const response = await getAssetGroupsTree()
        const rawTree: TreeNode[] = response.data.tree || []

        const addAssetsTotal = (nodes: TreeNode[]): number => {
          let total = 0
          nodes.forEach(n => {
            const childrenTotal = n.children ? addAssetsTotal(n.children) : 0
            const own = n.assets?.length ?? 0
            n.assetsTotal = own + childrenTotal
            total += n.assetsTotal
          })
          return total
        }

        addAssetsTotal(rawTree)
        this.tree = rawTree
        this.expanded = new Set()
      } catch (e: any) {
        this.error = e?.response?.data?.error || e?.message || this.$t('components.assetHierarchy.error')
      } finally {
        this.loading = false
      }
    }
  }
})
</script>

<style scoped lang="scss">
@import '@/assets/scss/asset-hierarchy-view.scss';
</style>
